import Textarea from "./textarea";
import {memo, useRef} from "react";
import {createPortal} from "react-dom";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _kebabCase from "lodash/kebabCase";
import {BRICKS as DEFAULT_BRICKS} from "./bricks";
import BrickConfigFields from "./rich-text/brick-config-fields";
import BrickConfiguratorPopover from "./rich-text/brick-configurator-popover";
import BrickInsertPopover from "./rich-text/brick-insert-popover";
import useRichTextBrickController from "./rich-text/use-rich-text-brick-controller";
import useRichTextCkEditorBridge from "./rich-text/use-rich-text-ckeditor-bridge";
import {
    RICH_TEXT_SETS
} from "./rich-text/brick-utils";

const RichText = memo((props) => {
    const bricks = props.bricks?.length
        ? props.bricks
        : DEFAULT_BRICKS;
    const defaultBrick = bricks[0] || null;
    const enableBricks = props.enableBricks !== undefined
        ? props.enableBricks
        : true;
    const showBrickHandle = props.showBrickHandle !== undefined
        ? props.showBrickHandle
        : 'configurable';
    const editorHostRef = useRef();
    const inputRef = useRef();
    const bridgeRef = useRef(null);
    const controller = useRichTextBrickController({
        bricks,
        insertOptions: props.insertOptions,
        enableBricks,
        defaultBrick,
        bridgeRef,
        editorHostRef
    });
    const bridge = useRichTextCkEditorBridge({
        bricks,
        disabled: props.disabled,
        enableBricks,
        inputRef,
        editorHostRef,
        toolBar: props.toolBar,
        toolBarSet: props.toolBarSet,
        value: props.value,
        showBrickHandle,
        handleInsertPanelKeyDown: controller.handleInsertPanelKeyDown,
        capturePendingSlashCommand: controller.capturePendingSlashCommand,
        syncSlashMenu: controller.syncSlashMenu,
        handleChipDoubleClick: controller.handleChipDoubleClick,
        handleSelectedBrickEnter: controller.handleSelectedBrickEnter,
        handleEditHandleClick: controller.handleEditHandleClick,
        handleVelocityBrickClick: controller.handleVelocityBrickClick,
        setToolbarHostNode: controller.setToolbarHostNode
    });
    bridgeRef.current = bridge;

    const ActiveBrickPanel = controller.activeBrick?.Panel;
    const configuratorBody = ActiveBrickPanel ? (
        <ActiveBrickPanel
            config={controller.brickConfig}
            setConfig={controller.setBrickConfig}
        />
    ) : controller.activeBrick?.configFields?.length ? (
        <BrickConfigFields
            brick={controller.activeBrick}
            config={controller.brickConfig}
            setConfig={controller.setBrickConfig}
        />
    ) : (
        <div className="text-sm text-gray-500">No configurator available.</div>
    );

    return (
        <div className={classNames(
            "rich-text rounded",
            `${_kebabCase(props.id)}`,
            props.autoGrow ? "rich-text-auto-grow" : null,
            enableBricks ? "relative" : null,
            props.disabled ? "pointer-events-none" : null,
            !props.disabled ? "select-auto" : null,
            props.modified
                ? 'focus:outline-2 focus:outline-dashed focus:outline-offset-2 focus:outline-fuchsia-400 outline-2 outline-dashed outline-offset-2 outline-fuchsia-400'
                : null
        )}>
            {enableBricks && (
                <>
                    {controller.toolbarHost && createPortal(
                        <button
                            type="button"
                            className="ck ck-button ck-off"
                            data-velocity-brick-button="true"
                            aria-label="Insert dynamic content"
                            title="Insert dynamic content"
                            onClick={() => controller.openBrickEditor('insert', null, null, {source: 'toolbar'})}
                        >
                            <span className="ck ck-icon">
                                <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M5.55 16.1h-.85a2.75 2.75 0 0 1-2.75-2.75V11.7c0-.78-.31-1.34-1.05-1.7.74-.36 1.05-.92 1.05-1.7V6.65A2.75 2.75 0 0 1 4.7 3.9h.85"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M14.45 3.9h.85a2.75 2.75 0 0 1 2.75 2.75V8.3c0 .78.31 1.34 1.05 1.7-.74.36-1.05.92-1.05 1.7v1.65a2.75 2.75 0 0 1-2.75 2.75h-.85"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M8.15 15.7 11.85 4.3"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.35"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                        </button>,
                        controller.toolbarHost
                    )}
                    <BrickInsertPopover
                        open={controller.insertModeOpen}
                        panelRef={controller.brickPanelRef}
                        panelStyle={controller.brickPanelStyle}
                        source={controller.brickInsertSource}
                        search={controller.brickSearch}
                        searchRef={controller.searchRef}
                        options={controller.filteredQuickOptions}
                        highlightedOptionId={controller.highlightedOptionId}
                        onSearchChange={controller.setBrickSearch}
                        onKeyDown={controller.handleInsertPanelKeyDown}
                        onOptionHover={controller.onHoverInsertOption}
                        onOptionSelect={controller.handleInsertOptionSelect}
                        onCancel={controller.onCancelInsertPopover}
                    />
                    <BrickConfiguratorPopover
                        open={controller.configureModeOpen}
                        panelRef={controller.brickPanelRef}
                        panelStyle={controller.brickPanelStyle}
                        title={controller.activeBrick?.title || 'Brick'}
                        description={controller.activeBrick?.configDescription ?? controller.activeBrick?.description}
                        body={configuratorBody}
                        submitLabel={controller.submitLabel}
                        submitDisabled={!controller.canApplyBrickConfig}
                        onCancel={controller.onCancelConfigurator}
                        onSubmit={controller.submitConfigurator}
                    />
                </>
            )}
            <input
                id={props.id}
                ref={inputRef}
                type="textarea"
                name={props.name}
                className="hidden"
                value={props.value}
                onChange={e => {
                    props.onChange(e, props.name, e.target.value);
                }}
                disabled={props.disabled}
                required={props.required}
                data-error-message={props.errorMessage}
            />
            {RICH_TEXT_SETS[props.toolBarSet] && (
                <style>{`
                    .rich-text.${_kebabCase(props.id)} .ck .ck-content.ck-editor__editable {
                        min-height: ${RICH_TEXT_SETS[props.toolBarSet].height}px;
                        max-height: ${RICH_TEXT_SETS[props.toolBarSet].height}px;
                    }
                `}</style>
            )}
            {(props.minHeight || props.maxHeight) && (
                <style>{`
                    .rich-text.${_kebabCase(props.id)} .ck .ck-content.ck-editor__editable {
                        ${props.minHeight ? `min-height: ${props.minHeight}px;` : ''}
                        ${props.maxHeight ? `max-height: ${props.maxHeight}px;` : ''}
                    }
                `}</style>
            )}
            {props.autoGrow && (
                <style>{`
                    .rich-text.rich-text-auto-grow .ck .ck-content.ck-editor__editable {
                        max-height: none;
                    }

                    .rich-text.rich-text-auto-grow .ck .ck-content.ck-editor__editable:not(.ck-focused):after {
                        content: none;
                    }
                `}</style>
            )}
            {bridge.loading && (
                <div className="flex w-full justify-center h-[183px]">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-500"
                         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                </div>
            )}
            <textarea ref={editorHostRef} className="hidden"/>
        </div>
    );
});

RichText.propTypes = {
    ...Textarea.propTypes,
    minHeight: PropTypes.number,
    maxHeight: PropTypes.number,
    autoGrow: PropTypes.bool,
};
RichText.defaultProps = {
    ...Textarea.defaultProps,
    minHeight: null,
    maxHeight: null,
    autoGrow: false,
};

RichText.DisplayValue = function (props) {
    return props.value === undefined || props.value === null ? '\u2014' : (
        <div dangerouslySetInnerHTML={{__html: props.value}}/>
    );
};

export default RichText;
